import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { uniqueId } from 'lodash'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { secToHourMinute } from '../../../utils/date'
import CATEGORY from '../../constants/category'

interface List {
  id: number
  values: Record<string, string | number | null>
}

interface Props {
  labels: Record<string, string>
  list: Record<string, string | number | null>[]
  displayLimit?: number
  labelGroup?: {
    join_column_count: number
    parent_label: string
  }[]
}

const TableList: FC<Props> = (props) => {
  const {
    labels,
    list,
    displayLimit,
    labelGroup,
  } = props

  const DISPLAY_LIMIT = 5

  const PAGINATION_DISPLAY_NUM = 5

  const [pageOffset, setPageOffset] = useState<number>(1)
  const [pageNum, setPageNum] = useState<number>(1)
  const [dataList, setDataList] = useState<{page:number, list:Record<string, string | number>[]}[]>()
  const [sortStatus, setSortStatus] = useState<{type:string, order:string}>({ type: "", order: "" })

  const tableHeight = useMemo(() => {
    if ((displayLimit || DISPLAY_LIMIT) < list.length) {
      return 46.8 * ((displayLimit || DISPLAY_LIMIT) + 1)
    }
    return 46.8 * ((list.length) + 1)
  }, [list])

  const arrayChunk = ([...array], size = 1) => (
    array.reduce((acc, _value, index) => (index % size ? acc : [...acc, array.slice(index, index + size)]), [])
  )

  const buildValue = (type: string, value: string | number | null) => {
    if (value === undefined || value === null || value === "") {
      return "-"
    }
    if (type === "category_name") {
      return CATEGORY.find((val) => val.code === value)?.title
    }
    if (type.includes("days")) {
      return `${value.toLocaleString()}日`
    }
    if (type.includes("times")) {
      return `${value.toLocaleString()}回`
    }
    if (type.includes("pages")) {
      return `${value.toLocaleString()}枚`
    }
    if (type.includes("use_count")) {
      return `${value.toLocaleString()}人`
    }
    if (type.includes("biz")) {
      return `${value.toLocaleString()}biz`
    }
    if (type.includes("time")) {
      return secToHourMinute(value as number)
    }
    if (type.includes("percent")) {
      return `${value}%`
    }
    return value
  }

  const pagination = () => {
    const displayNum = pageNum < PAGINATION_DISPLAY_NUM ? pageNum : PAGINATION_DISPLAY_NUM
    return (
      <div
        className="pagination"
      >
        <div
          onClick={pageOffset === 1 ? undefined : () => setPageOffset(pageOffset - 1)}
          role="none"
          className={`page ${pageOffset === 1 ? "disabled" : ""}`}
        >
          &lt;
        </div>
        {
          pageOffset <= Math.ceil(displayNum / 2) && (
            Array(displayNum).fill(null).map((v, i) => (
              <div
                onClick={() => setPageOffset(i + 1)}
                role="none"
                className={`page ${pageOffset === i + 1 ? "selected" : ""}`}
                key={uniqueId()}
              >
                {i + 1}
              </div>
            ))
          )
        }
        {
          pageOffset > Math.ceil(displayNum / 2) && pageNum - pageOffset > Math.floor(displayNum / 2) && (
            Array(displayNum).fill(null).map((v, i) => (
              <div
                onClick={() => setPageOffset(pageOffset - Math.floor(displayNum / 2) + i)}
                role="none"
                className={`page ${pageOffset === pageOffset - Math.floor(displayNum / 2) + i ? "selected" : ""}`}
                key={uniqueId()}
              >
                {pageOffset - Math.floor(displayNum / 2) + i}
              </div>
            ))
          )
        }
        {
          pageOffset > Math.ceil(displayNum / 2) && pageNum - pageOffset <= Math.floor(displayNum / 2) && (
            Array(displayNum).fill(null).map((v, i) => (
              <div
                onClick={() => setPageOffset(pageNum - displayNum + 1 + i)}
                role="none"
                className={`page ${pageOffset === pageNum - displayNum + 1 + i ? "selected" : ""}`}
                key={uniqueId()}
              >
                {pageNum - displayNum + 1 + i}
              </div>
            ))
          )
        }
        <div
          onClick={pageOffset === pageNum ? undefined : () => setPageOffset(pageOffset + 1)}
          role="none"
          className={`page ${pageOffset === pageNum ? "disabled" : ""}`}
        >
          &gt;
        </div>
      </div>
    )
  }

  const sortResult = (key_name: string, sort?: "ASC" | "DESC") => {
    if (!key_name) return
    const order = sort || "ASC"
    let line = []
    if (order === "ASC") {
      line = [...list].sort((val_1, val_2) => {
        if ((val_1[key_name] || 0) < (val_2[key_name] || 0)) return -1
        if ((val_1[key_name] || 0) > (val_2[key_name] || 0)) return 1
        return 0
      })
      setSortStatus({ type: key_name, order: "ASC" })
    } else {
      line = [...list].sort((val_1, val_2) => {
        if ((val_1[key_name] || 0) < (val_2[key_name] || 0)) return 1
        if ((val_1[key_name] || 0) > (val_2[key_name] || 0)) return -1
        return 0
      })
      setSortStatus({ type: key_name, order: "DESC" })
    }
    setDataList(
      arrayChunk(line, (displayLimit || DISPLAY_LIMIT))
        .map((value: Record<string, string | number>, key: number) => ({ page: key + 1, list: value })),
    )
    setPageOffset(1)
  }

  const sortBlock = (title: string, type: string) => {
    const order = ((sortStatus.type !== type) || (sortStatus.type === type && sortStatus.order === 'DESC')) ? "ASC" : "DESC"
    return (
      <>
        <span
          onClick={() => sortResult(type, order)}
          role="none"
          className="sort_button"
        >
          {title}
        </span>
        <div className="sort_icon_area">
          {
            sortStatus.type === type && sortStatus.order === 'DESC' && (
              <span
                onClick={() => sortResult(type, "ASC")}
                role="none"
                className="sort_button"
              >
                <FontAwesomeIcon icon={['fas', 'sort-amount-down']} />
              </span>
            )
          }
          {
            sortStatus.type === type && sortStatus.order === 'ASC' && (
              <span
                onClick={() => sortResult(type, "DESC")}
                role="none"
                className="sort_button"
              >
                <FontAwesomeIcon icon={['fas', 'sort-amount-down-alt']} />
              </span>
            )
          }
          {
            sortStatus.type !== type && (
              <span
                onClick={() => sortResult(type, "DESC")}
                role="none"
                className="sort_button"
              >
                <FontAwesomeIcon icon={['fas', 'sort']} />
              </span>
            )
          }
        </div>
      </>
    )
  }

  const nestedHeader = () => {
    if (!labelGroup) return <></>
    const label_keys = Object.keys(labels)
    let display_count = 0
    return (
      labelGroup.map((label) => (
        <th
          key={uniqueId('th_')}
          style={{
            width: `${(100 / labelGroup.length)}%`,
          }}
          colSpan={label.join_column_count}
        >
          {label.parent_label}
          <div
            className="group"
            style={{
              marginTop: label.parent_label ? "16px" : 0,
            }}
          >
            {
              Array(label.join_column_count).fill(null).map(() => {
                const key = label_keys[display_count]
                display_count += 1
                return (
                  <div
                    key={uniqueId('value_')}
                    className="table_header"
                  >
                    {sortBlock(labels[key], key)}
                  </div>
                )
              })
            }
          </div>
        </th>
      ))
    )
  }

  const nestedRow = (value: Record<string, string | number>) => {
    if (!labelGroup) return <></>
    const label_keys = Object.keys(labels)
    let display_count = 0
    return (
      labelGroup.map((label) => (
        <td
          key={uniqueId('td_')}
          style={{
            width: `${(100 / labelGroup.length)}%`,
          }}
          colSpan={label.join_column_count}
        >
          <div className="group">
            {
              Array(label.join_column_count).fill(null).map(() => {
                const key = label_keys[display_count]
                display_count += 1
                return (
                  <div
                    key={uniqueId('value_')}
                    style={{
                      width: `${100 / label.join_column_count}%`,
                    }}
                  >
                    {buildValue(key, value[key])}
                  </div>
                )
              })
            }
          </div>
        </td>
      ))
    )
  }

  useEffect(() => {
    const array = arrayChunk(list, (displayLimit || DISPLAY_LIMIT)).map((val: List, key: number) => ({ page: key + 1, list: val }))
    setDataList(array)
    setPageNum(array.length)
  }, [])

  return (
    <div className="table_list_wrap">
      <div
        className="table_list"
        style={{
          minHeight: `${tableHeight}px`,
        }}
      >
        <table>
          <thead>
            <tr>
              {
                labelGroup?.length ? (
                  <>
                    {nestedHeader()}
                  </>
                ) : (
                  <>
                    {
                      Object.keys(labels).map((value, index) => (
                        <th
                          key={`th_${value}_${index + 0}`}
                          style={{
                            width: `${(100 / Object.keys(labels).length)}%`,
                          }}
                        >
                          <div className="table_header">
                            {sortBlock(labels[value], value)}
                          </div>
                        </th>
                      ))
                    }
                  </>
                )
              }
            </tr>
          </thead>
          <tbody>
            {
              dataList?.find((value) => value.page === pageOffset)?.list.map((value, index) => (
                <tr key={`tr_${value.id}_${index + 0}`}>
                  {
                    labelGroup?.length ? (
                      <>
                        {nestedRow(value)}
                      </>
                    ) : (
                      <>
                        {
                          Object.keys(labels).map((val) => (
                            <td key={`td_${value.id}_${val}`}>
                              {buildValue(val, value[val])}
                            </td>
                          ))
                        }
                      </>
                    )
                  }
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>
      {
        pageNum > 1 && (
          pagination()
        )
      }
    </div>
  )
}

TableList.defaultProps = {
  displayLimit: undefined,
  labelGroup: undefined,
}

export default TableList
